import { SearchableSelect, SearchableSelectOption } from "@comps/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { ObjectInfos } from "@dto/objects";
import { ASC, DESC, Sorting, SortType, View } from "@dto/views";
import { BASE_URL } from "@utils/constants";
import { getPathKeys, getSrcFieldKey, isSortable } from "@utils/model";
import { toastError } from "@utils/toasts";
import { TW_FilterEditorNewFilter, TW_FilterEditorRemoveFilter, TW_FilterEditorRemoveFilterDisabled, TW_SortEditor, TW_SortEditorPathSelect, TW_SortEditorSortSelect } from "@utils/twClasses";
import { put } from "@utils/utils";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore } from "src/store";


interface SortingEditorProps {
    sortings: Sorting[]
    disabled: boolean
    onClose: () => void
}

function SortingEditor({ sortings, disabled, onClose }: SortingEditorProps) {

    const workspace = useModelStore((state) => state.selectedWorkspace);
    const updateSelectedView = useModelStore((state) => state.updateSelectedView);
    const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
    const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
    const selectedView = useModelStore((state) => state.selectedView);
    const { t } = useTranslation();

    const fields = selectedObjectInfos?.fields;
    if (!fields) {
        return null;
    }

    const sortingPathKeys: string[] = [];
    sortings.forEach((sorting) => {
        sortingPathKeys.push(sorting.pathKey);
    })

    const onViewChange = (objectInfos: ObjectInfos, view: View) => {
        updateSelectedView(objectInfos, view, null, sharedObjectInfos);
        // No need to call fetchView as it is triggered by the view change
        // fetchView(view);
    }


    const onRemoveSorting = (sortPathKey: string) => {
        if (!sortPathKey || disabled) {
            return;
        }

        const newSortings = (sortings || []).filter(s => s.pathKey !== sortPathKey);
        validateAllSortings(newSortings);
    }

    function validateSorting(sorting: Sorting | null) {
        if (!sorting) {
            return;
        }

        const newSortings = (sortings || []).map(s => {
            if (s.pathKey === sorting.pathKey) {
                return sorting;
            }
            return s;
        });

        const newSorting = newSortings.find(s => s.pathKey === sorting.pathKey);
        if (!newSorting) {
            newSortings.push(sorting);
        }

        validateAllSortings(newSortings);
    }

    function validateAllSortings(sortings: Sorting[] | null) {
        if (!workspace?._key) {
            console.error("Workspace key is not set");
            return;
        }
        const newView: View = {
            ...selectedView!,
            sortings: sortings
        }
        put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos?.object?._key}/views`, newView).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                    onClose();
                }
            } else {
                onViewChange(selectedObjectInfos!, newView);
            }
        })
    }

    // const sortedSortingByPathKeys = Array.from(sortingByPathKeys.keys()).sort();
    const itemKeyPref = selectedView?._key + "-sorting-"
    if (disabled) {
        return (
            <TooltipProvider>
                <Tooltip delayDuration={100}>
                    <TooltipTrigger>
                        <div className={TW_SortEditor}>
                            {sortings.length > 0 &&
                                sortings.map((sorting, index) => {
                                    return <SortingItem key={itemKeyPref + index} sorting={sorting} index={index} validateSorting={validateSorting} onRemoveSorting={onRemoveSorting} usedSortingPathKeys={sortingPathKeys} disabled={disabled} onClose={onClose} />
                                })}
                            <SortingItem key={itemKeyPref + sortings.length} sorting={null} index={sortings.length} validateSorting={validateSorting} onRemoveSorting={onRemoveSorting} usedSortingPathKeys={sortingPathKeys} disabled={disabled} onClose={onClose} />
                        </div>
                    </TooltipTrigger>
                    <TooltipContent>
                        {t("sorting.edition.forbidden.tooltip")}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        )
    } else {
        return (
            <div className={TW_SortEditor}>
                {sortings.length > 0 &&
                    sortings.map((sorting, index) => {
                        return <SortingItem key={itemKeyPref + index} sorting={sorting} index={index} validateSorting={validateSorting} onRemoveSorting={onRemoveSorting} usedSortingPathKeys={sortingPathKeys} disabled={disabled} onClose={onClose} />
                    })}
                <SortingItem key={itemKeyPref + sortings.length} sorting={null} index={sortings.length} validateSorting={validateSorting} onRemoveSorting={onRemoveSorting} usedSortingPathKeys={sortingPathKeys} disabled={disabled} onClose={onClose} />
            </div>
        )
    }
}

interface SortingItemProps {
    sorting: Sorting | null,
    index: number,
    validateSorting: (sorting: Sorting) => void,
    onRemoveSorting: (sortPathKey: string) => void,
    usedSortingPathKeys: string[],
    disabled: boolean,
    onClose: () => void
}

function SortingItem({ sorting, index, validateSorting, onRemoveSorting, usedSortingPathKeys, disabled, onClose }: SortingItemProps) {

    const { t } = useTranslation();
    const selectedView = useModelStore((state) => state.selectedView);
    const [selectedPathKey, setSelectedPathKey] = useState<string | undefined>(undefined);
    const [selectedSortType, setSelectedSortType] = useState<SortType>(ASC);
    const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
    const relationByKeys = useModelStore((state) => state.relationByKeys);
    const fieldByKeys = useModelStore((state) => state.fieldByKeys);

    useEffect(() => {
        if (sorting) {
            const fields = selectedObjectInfos?.fields;
            if (!fields) {
                return;
            }
            const pathKeys = getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, true);
            const selectedPathKeyIndex = pathKeys.findIndex(pathKey => pathKey === sorting.pathKey);
            if (selectedPathKeyIndex > -1) {
                setSelectedPathKey(sorting.pathKey);
            } else {
                const fieldKey = getSrcFieldKey(sorting.pathKey);
                setSelectedPathKey(fieldKey?.fieldKey);
            }

            setSelectedSortType(sorting.sort);
        }
    }, [sorting]);

    const onPathChange = (pathKey: string) => {
        setSelectedPathKey(pathKey);

        const newSorting: Sorting = {
            pathKey: pathKey,
            sort: selectedSortType
        }
        validateSorting(newSorting);
    }

    const onSortTypeChange = (sortType: SortType) => {
        setSelectedSortType(sortType);

        if (!selectedPathKey) {
            return;
        }

        const newSorting: Sorting = {
            pathKey: selectedPathKey,
            sort: sortType
        }
        validateSorting(newSorting);
    }

    const onRemoveCurrentSorting = () => {
        if (!selectedPathKey || disabled) {
            return;
        }
        onRemoveSorting(selectedPathKey);
    }

    const fields = selectedObjectInfos?.fields;
    if (!fields) {
        return null;
    }
    const pathKeys = getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, false);
    const sortableByPathKey = new Map<string, boolean>();
    pathKeys.forEach((pathKey, index) => {
        if (!pathKey) {
            return;
        }
        const field = fields[index];
        const sortable = isSortable(field.type)
        sortableByPathKey.set(pathKey, sortable)
    });

    const disabledTrash = disabled || !selectedPathKey;

    let divKey = selectedView?._key + "-sorting-" + index + "-" + selectedPathKey;
    if (selectedPathKey) {
        divKey += "-" + selectedSortType;
    } else {
        divKey += "-new";
    }
    return (
        <div className={TW_FilterEditorNewFilter} key={divKey}>
            <SearchableSelect
                className={TW_SortEditorPathSelect}
                placeholder={t("sorting.path.placeholder")}
                value={selectedPathKey}
                onValueChange={onPathChange}
                disabled={disabled || selectedPathKey != undefined}
                options={pathKeys.flatMap((pathKey, index): SearchableSelectOption[] => {
                    const field = fields[index];
                    if (!pathKey) {
                        return [{ value: field._key, label: field.name + " (" + t("group.field.invalid") + ")", disabled: true }];
                    }
                    if (!sortableByPathKey.get(pathKey)) {
                        return [];
                    }
                    const disabled = usedSortingPathKeys.includes(pathKey);
                    return [{ value: pathKey, label: field.name, disabled: disabled }];
                })}
            />
            <Select onValueChange={onSortTypeChange} value={selectedSortType} disabled={disabled || !selectedPathKey}>
                <SelectTrigger className={TW_SortEditorSortSelect}>
                    <SelectValue placeholder={t("sorting.type.placeholder")} />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem key={divKey + "sort-type-asc"} value={ASC}>{t("sorting.type.name." + ASC)}</SelectItem>
                    <SelectItem key={divKey + "sort-type-desc"} value={DESC}>{t("sorting.type.name." + DESC)}</SelectItem>
                </SelectContent>
            </Select>
            {!disabledTrash && <Trash2 className={TW_FilterEditorRemoveFilter} onClick={onRemoveCurrentSorting} />}
            {disabledTrash && <Trash2 className={TW_FilterEditorRemoveFilterDisabled} />}
        </div>
    )
}

export default SortingEditor;
